import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useWards,
  useCreateWard,
  useUpdateWard,
  useDeleteWard,
  useBulkDeleteWards,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { WardFormModal, WardFormData } from '../../components/ui/WardFormModal';
import { WardDetailsModal } from '../../components/ui/WardDetailsModal';
import { FacilityTabs } from '../../components/clinical/FacilityTabs';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  AlertCircle,
  DoorOpen,
  BedDouble,
  Activity,
  Hotel,
  Layers,
  Stethoscope,
  Baby,
  HeartPulse,
  ShieldAlert,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';

const wardTypeIcons: Record<string, React.ElementType> = {
  GENERAL: Hotel,
  SEMI_PRIVATE: Layers,
  PRIVATE: Building2,
  ICU: Stethoscope,
  NICU: Baby,
  CCU: HeartPulse,
  HDU: ShieldAlert,
};

const wardTypeColors: Record<string, string> = {
  GENERAL: 'bg-blue-100 text-blue-700',
  SEMI_PRIVATE: 'bg-indigo-100 text-indigo-700',
  PRIVATE: 'bg-emerald-100 text-emerald-700',
  ICU: 'bg-red-100 text-red-700',
  NICU: 'bg-pink-100 text-pink-700',
  CCU: 'bg-rose-100 text-rose-700',
  HDU: 'bg-amber-100 text-amber-700',
};

const wardTypeOptions = ['GENERAL', 'SEMI_PRIVATE', 'PRIVATE', 'ICU', 'NICU', 'CCU', 'HDU'] as const;

const wardTypeLabels: Record<string, string> = {
  GENERAL: 'General',
  SEMI_PRIVATE: 'Semi Private',
  PRIVATE: 'Private',
  ICU: 'ICU',
  NICU: 'NICU',
  CCU: 'CCU',
  HDU: 'HDU',
};

type WardTab = 'all' | 'active' | 'inactive';

// Tabs are driven by the `?tab=` URL param so a view can be bookmarked/shared.
const WARD_TABS: { key: WardTab; label: string; icon: React.ElementType }[] = [
  { key: 'all', label: 'All', icon: Layers },
  { key: 'active', label: 'Active', icon: Activity },
  { key: 'inactive', label: 'Inactive', icon: X },
];

const WARD_TAB_STATUS: Record<WardTab, string> = {
  all: '',
  active: 'active',
  inactive: 'inactive',
};

const WARD_STATUS_TAB: Record<string, WardTab> = {
  '': 'all',
  active: 'active',
  inactive: 'inactive',
};

interface Ward {
  id: string;
  wardCode: string;
  wardName: string;
  wardType: string;
  rooms?: {
    id: string;
    name: string;
    floor?: { id: string; name: string; floorNumber?: number } | null;
  }[];
  floor?: string;
  totalBeds: number;
  isActive: boolean;
}

export function Wards() {
  const limit = 20;

  const [showForm, setShowForm] = useState(false);
  const [editingWard, setEditingWard] = useState<Ward | null>(null);
  const [viewWard, setViewWard] = useState<Ward | null>(null);

  // List controls
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: WardTab = tabParam && tabParam in WARD_TAB_STATUS ? (tabParam as WardTab) : 'all';
  const statusFilter = WARD_TAB_STATUS[tab];

  /** Keeps the active tab in the URL so it can be bookmarked/shared. */
  const setTab = (next: WardTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const queryFilters = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      wardType: typeFilter || undefined,
      isActive: statusFilter === '' ? undefined : statusFilter === 'active',
    }),
    [debouncedSearch, typeFilter, statusFilter],
  );
  const activeFilterCount = (typeFilter ? 1 : 0) + (statusFilter ? 1 : 0);

  const { data: wardsData, isLoading, error } = useWards(page, limit, queryFilters);
  const createWard = useCreateWard();
  const updateWard = useUpdateWard();
  const deleteWard = useDeleteWard();
  const bulkDeleteWard = useBulkDeleteWards();

  const wards = useMemo(() => wardsData?.data || [], [wardsData]);
  const meta = wardsData?.meta;

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, statusFilter]);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Ward | null>(null);

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(wards.map((ward: Ward) => ward.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [wards]);

  const allSelected = wards.length > 0 && wards.every((ward: Ward) => selectedIds.has(ward.id));
  const someSelected = wards.some((ward: Ward) => selectedIds.has(ward.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = wards.length > 0 && wards.every((ward: Ward) => prev.has(ward.id));
      return everySelected ? new Set() : new Set(wards.map((ward: Ward) => ward.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('');
    setTab('all');
    setPage(1);
  };

  const handleOpenAdd = () => {
    setEditingWard(null);
    setShowForm(true);
  };

  const handleOpenEdit = (ward: Ward) => {
    setEditingWard(ward);
    setShowForm(true);
  };

  const handleEditFromDetails = (ward: Ward) => {
    setViewWard(null);
    handleOpenEdit(ward);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingWard(null);
  };

  const handleSubmit = (data: WardFormData) => {
    if (editingWard) {
      updateWard.mutate(
        { id: editingWard.id, data },
        { onSuccess: handleCloseForm }
      );
    } else {
      createWard.mutate(data, { onSuccess: handleCloseForm });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteWard.mutateAsync(deleteTarget.id);
      toast.success(`Deleted ${deleteTarget.wardName}`);
    } catch {
      toast.error('Failed to delete ward');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteWard.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} ward${ids.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete some wards');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Wards</h1>
        </div>
        <button
          onClick={handleOpenAdd}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add Ward
        </button>
      </div>

      {/* Tabs: route tabs left, status filter tabs right */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Route tabs (Wards / Beds) */}
        <FacilityTabs />

        {/* Status tabs */}
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-xl border border-border bg-surface p-1">
          {WARD_TABS.map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-text-muted hover:bg-background hover:text-text'
                }`}
              >
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load wards</span>
        </div>
      )}

      {/* Search + Filter Bar */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by ward name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`px-3 py-2 border rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${
              showFilters || activeFilterCount > 0
                ? 'border-primary text-primary bg-primary/5'
                : 'border-border text-text-muted hover:text-text hover:bg-background'
            }`}
          >
            <SlidersHorizontal size={14} />
            Filter
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[0.625rem] font-semibold">
                {activeFilterCount}
              </span>
            )}
          </button>
          {(search || activeFilterCount > 0) && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Ward Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">All types</option>
                {wardTypeOptions.map((t) => (
                  <option key={t} value={t}>{wardTypeLabels[t]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setTab(WARD_STATUS_TAB[e.target.value] ?? 'all')}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteWard.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteWard.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteWard.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={wards.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Ward</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Capacity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {wards.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                      {search || activeFilterCount > 0 ? (
                        <span>
                          No wards match your filters.{' '}
                          <button onClick={clearFilters} className="text-primary hover:underline">
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No wards configured. Add your first ward to get started.'
                      )}
                    </td>
                  </tr>
                ) : (
                  wards.map((ward: Ward) => {
                    const TypeIcon = wardTypeIcons[ward.wardType] || Building2;
                    return (
                      <tr key={ward.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(ward.id)}
                            onChange={() => toggleOne(ward.id)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                              <Building2 size={16} className="text-primary" />
                            </div>
                            <div>
                              <button
                                type="button"
                                onClick={() => setViewWard(ward)}
                                className="text-sm font-medium text-text hover:text-primary text-left transition-colors"
                                title="View ward details"
                              >
                                {ward.wardName}
                              </button>
                              <p className="text-xs text-text-muted font-mono">{ward.wardCode}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${wardTypeColors[ward.wardType] || 'bg-background text-text-muted'}`}>
                            <TypeIcon size={12} />
                            {ward.wardType.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-sm text-text">
                            <DoorOpen size={14} className="text-text-muted" />
                            {ward.rooms && ward.rooms.length > 0
                              ? `${ward.rooms.length} room${ward.rooms.length === 1 ? '' : 's'}`
                              : ward.floor || '—'}
                          </div>
                          {ward.rooms && ward.rooms.length > 0 && (
                            <p className="text-xs text-text-muted pl-[22px] max-w-[220px] truncate">
                              {ward.rooms.map((room) => room.name).join(', ')}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-sm text-text">
                            <BedDouble size={14} className="text-text-muted" />
                            {ward.totalBeds} beds
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${ward.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                            <Activity size={12} />
                            {ward.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleOpenEdit(ward)}
                              className="p-2 hover:bg-background rounded-lg text-text-muted hover:text-primary transition-colors"
                              title="Edit ward"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(ward)}
                              className="p-2 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors"
                              title="Delete ward"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="wards"
          />
        </div>
      )}

      <WardFormModal
        isOpen={showForm}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        initialData={
          editingWard
            ? {
                ...editingWard,
                wardType: editingWard.wardType as WardFormData['wardType'],
                roomIds: editingWard.rooms?.map((room) => room.id) ?? [],
              }
            : undefined
        }
        isLoading={createWard.isPending || updateWard.isPending}
      />

      <WardDetailsModal
        ward={viewWard}
        onClose={() => setViewWard(null)}
        onEdit={handleEditFromDetails}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete "${deleteTarget?.wardName || ''}"?`}
        description="This action will remove the selected ward. This cannot be undone."
        confirmLabel="Delete ward"
        loading={deleteWard.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} ward${selectedIds.size === 1 ? '' : 's'}?`}
        description="This action will remove the selected wards. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} ward${selectedIds.size === 1 ? '' : 's'}`}
        loading={bulkDeleteWard.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
